<template>
  <div :class="['panel-root rounded-lg border bg-white', sizeClass]">
    <div class="flex items-center justify-between mb-2">
      <h3 class="text-base font-semibold" v-if="title">{{ title }}</h3>
      <div class="flex items-center gap-2">
        <slot name="extra" />
        <el-button v-if="collapsible" size="small" text @click="toggleCollapse">
          {{ collapsed ? '展开公式' : '收起公式' }}
        </el-button>
      </div>
    </div>

    <el-collapse-transition>
      <div v-show="!collapsed">
        <div class="space-y-2">
          <div v-for="(html, idx) in formulaHtmlList" :key="idx" class="overflow-x-auto">
            <div class="katex-block" v-html="html"></div>
          </div>
        </div>
        <div v-if="variables?.length" class="variables mt-3 text-sm text-gray-700">
          <div class="font-medium mb-1">变量说明</div>
          <ul class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
            <li v-for="(v,i) in variables" :key="i">
              <span class="font-mono">{{ v.symbol }}</span>
              <span class="mx-1">=</span>
              <span>{{ v.label }}</span>
              <span v-if="v.desc" class="text-gray-500">（{{ v.desc }}）</span>
            </li>
          </ul>
        </div>
        <div v-if="notes?.length" class="mt-2 text-xs text-gray-500 space-y-1">
          <div v-for="(n,i) in notes" :key="i">• {{ n }}</div>
        </div>
      </div>
    </el-collapse-transition>
  </div>
  
</template>

<script setup>
import katex from 'katex'
import 'katex/dist/katex.min.css'
import { computed, onMounted, ref } from 'vue'

const props = defineProps({
  title: { type: String, default: '' },
  formulas: { type: [String, Array], default: () => [] },
  variables: { type: Array, default: () => [] },
  notes: { type: Array, default: () => [] },
  // 是否允许折叠；移动端默认建议折叠
  collapsible: { type: Boolean, default: true },
  // 默认折叠（桌面端也生效）
  defaultCollapsed: { type: Boolean, default: false },
  // 在移动端自动折叠（优先于 defaultCollapsed）
  autoCollapseOnMobile: { type: Boolean, default: true },
  // 紧凑模式：减小内边距与字体
  size: { type: String, default: 'normal' } // 'normal' | 'compact'
})

const formulaHtmlList = computed(() => {
  const list = Array.isArray(props.formulas) ? props.formulas : (props.formulas ? [props.formulas] : [])
  return list.map(f => katex.renderToString(String(f), { throwOnError: false, displayMode: true }))
})

// 移动端自适应折叠
const collapsed = ref(false)
const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768
onMounted(() => {
  collapsed.value = props.defaultCollapsed || (props.autoCollapseOnMobile && isMobile())
})
const toggleCollapse = () => { if (props.collapsible) collapsed.value = !collapsed.value }

const sizeClass = computed(() => props.size === 'compact' ? 'p-3' : 'p-4')

</script>

<style scoped>
.katex-block {
  /* allow horizontal scroll for long equations */
  overflow-x: auto;
}

/* KaTeX 在小屏缩放一些，并压缩上下间距 */
@media (max-width: 768px) {
  .panel-root .katex { font-size: 0.95rem; }
  .panel-root .katex-display { margin: 0.25rem 0; }
}

/* 紧凑模式进一步减小字号与行距（可选） */
.panel-root.p-3 .katex { font-size: 0.95rem; }
.panel-root.p-3 .variables { font-size: 0.9rem; }
</style>
